import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Box, Button, Container, Divider, Menu, MenuItem, TextField, Toolbar, Tooltip, useScrollTrigger } from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import NavDropdownLinks from './NavDropdownLinks';
import AppTitleLogo from '../../assets/images/projam-icon.svg';
import "./topbar.scss";
import APP_BASE_URL from "../../components/config/appConfig";
import CreateBacklogIssue from '../Create_Backlogissue';
import { jwtDecode } from "jwt-decode";
import BackgroundLetterAvatarsLogo from '../AvatarLogo';
import NotificationComponent from '../Modal/NotificationModel';
import { useToast } from '../Toast';
import { useCurrentUser } from '../CurrentUser';
import { useSearchBar } from '../../context/PageSearchContext';

const settings = ["Profile"];

/* one glyph per signed-in destination; the public site links (Features,
   Pricing, About us, Contact us) are plain text */
const NAV_ICONS = {
    Projects: FolderRoundedIcon,
    Teams: GroupsRoundedIcon,
};

const navIconFor = (text) => NAV_ICONS[text] || FolderRoundedIcon;

const decodeToken = (token) => {
    try {
        return token ? jwtDecode(token) : {};
    } catch (error) {
        return {};
    }
};

const isTypingTarget = (target) =>
    Boolean(target) && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);

function Header(props) {
    const { pages = [], isUserLoggedIn = false, allProjectsList } = props
    const [anchorElNav, setAnchorElNav] = useState(null);
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [createModal, setCreateModel] = useState(false);
    const searchRef = useRef(null);
    const { Projects } = useSelector((state) => state.ProjamAppSlice);
    const location = useLocation()
    const navigate = useNavigate()
    const toast = useToast()
    const { user: currentUser } = useCurrentUser()
    /* the box belongs to whichever page is on screen; it says what it filters */
    const { query: search, setQuery: setSearch, placeholder: searchScope, isSearchable } = useSearchBar()
    // a quiet shadow only once content scrolls underneath the bar
    const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 4 });

    const token = localStorage.getItem('accessToken')
    const claims = useMemo(() => decodeToken(token), [token]);
    const displayName = currentUser?.name || claims?.name || 'user';
    const displayEmail = currentUser?.email || claims?.email || '';

    const selectedProjectId = Projects?.selectedProject?.selectedProjectBackLogId

    /* /projects/<slug>/... - "profile" lives under the same prefix but is not a project */
    const projectSlug = useMemo(() => {
        const [, root, slug] = location.pathname.split('/');
        return root === 'projects' && slug && slug !== 'profile' ? slug : '';
    }, [location.pathname]);

    /* Pages that have nothing to filter (reports, settings, profile) still get a
       working box: Enter carries the term to the All sprints page for this project,
       or to the projects list when no project is open. */
    const handOffSearch = () => {
        const term = search.trim();
        if (!term) return;
        const to = projectSlug ? `/projects/${projectSlug}/all-sprints` : '/projects';
        navigate(`${to}?q=${encodeURIComponent(term)}`);
    };

    const onChangeModalVerifyState = () => {
        setCreateModel(true);
    }

    /* "/" jumps to search from anywhere that isn't already a text field */
    useEffect(() => {
        if (!isUserLoggedIn) return undefined;
        const onKeyDown = (event) => {
            if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
            if (isTypingTarget(event.target)) return;
            event.preventDefault();
            searchRef.current?.focus();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isUserLoggedIn]);

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const onLogout = async () => {
        try {
            const response = await fetch(`${APP_BASE_URL}/users/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                localStorage.removeItem('accessToken');
                toast.info('You have been signed out');
                navigate('/');
            } else {
                const errorMessage = await response.text();
                console.log('Logout failed:', errorMessage);
                toast.error('Could not sign you out. Please try again.');
            }
        } catch (error) {
            console.error('An error occurred during logout:', error);
            toast.error('Could not sign you out. Please try again.');
        }
    };

    const handleMenuItemClick = (setting) => {
        if (setting === "Profile") {
            navigate('/projects/profile');
            setAnchorElUser(null);
        }
    };

    const mobileItems = [
        ...pages.map((page, index) => {
            const Icon = navIconFor(page.text);
            const isDisabled = isUserLoggedIn && page.text === "Projects" && !selectedProjectId;
            const content = (
                <>
                    {isUserLoggedIn ? <span className="tb-menu__icon"><Icon /></span> : null}
                    <span className="tb-menu__text">{page.text}</span>
                </>
            );
            return isUserLoggedIn ? (
                <MenuItem key={page.text} onClick={handleCloseNavMenu} disabled={isDisabled} className="tb-menu__item" style={{ '--i': index }}>
                    {content}
                </MenuItem>
            ) : (
                <MenuItem
                    key={page.text}
                    component={NavLink}
                    to={page.navUrl}
                    onClick={handleCloseNavMenu}
                    className={`tb-menu__item ${location.pathname === page.navUrl ? 'is-current' : ''}`}
                    style={{ '--i': index }}
                >
                    {content}
                </MenuItem>
            );
        }),
        ...(isUserLoggedIn
            ? [
                <Divider key="divider" />,
                <MenuItem
                    key="create"
                    className="tb-menu__item is-accent"
                    style={{ '--i': pages.length }}
                    onClick={() => {
                        handleCloseNavMenu();
                        onChangeModalVerifyState();
                    }}
                >
                    <span className="tb-menu__icon"><AddRoundedIcon /></span>
                    <span className="tb-menu__text">Create issue</span>
                </MenuItem>,
            ]
            : []),
    ];

    return (
        <AppBar
            position="fixed"
            elevation={0}
            className={`appbar-main-cls ${scrolled ? 'is-scrolled' : ''}`}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
            <Container maxWidth="xl" className="tb-container">
                <Toolbar disableGutters className="tb-toolbar">
                    {/* ------------------------------------------------ mobile menu */}
                    <Box className="tb-mobile" sx={{ display: { xs: 'flex', lg: 'none' } }}>
                        <button
                            type="button"
                            className={`tb-icon-btn ${anchorElNav ? 'is-open' : ''}`}
                            aria-label="Open navigation"
                            aria-controls="tb-mobile-menu"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                        >
                            <MenuRoundedIcon />
                        </button>
                        <Menu
                            id="tb-mobile-menu"
                            anchorEl={anchorElNav}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            keepMounted
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            slotProps={{ paper: { className: 'tb-menu' } }}
                            sx={{ display: { xs: 'block', lg: 'none' } }}
                        >
                            {mobileItems}
                        </Menu>
                    </Box>

                    {/* --------------------------------------------------------- brand */}
                    <a href="/" className="tb-brand" aria-label="Projam home">
                        <span className="tb-brand__mark">
                            <img src={AppTitleLogo} alt="" height={30} />
                        </span>
                        <span className="tb-brand__text">Projam</span>
                    </a>

                    {/* ------------------------------------- desktop nav (signed out)
                        The public site links sit dead centre of the bar; signed-in
                        navigation lives on the right, next to the search box. */}
                    {!isUserLoggedIn && (
                        <Box className="tb-nav tb-nav--center" sx={{ display: { xs: 'none', lg: 'flex' } }}>
                            {pages.map((page) => (
                                <NavLink
                                    key={page.text}
                                    to={page.navUrl}
                                    className={({ isActive }) => `tb-link tb-link--plain ${isActive ? 'active' : ''}`}
                                >
                                    {page.text}
                                </NavLink>
                            ))}
                        </Box>
                    )}

                    {/* -------------------------------------------------------- actions */}
                    <Box className="tb-actions">
                        {isUserLoggedIn ? (
                            <>
                                {/* project switcher rides with the other tools, left of search */}
                                <Box className="tb-nav tb-nav--actions" sx={{ display: { xs: 'none', lg: 'flex' } }}>
                                    {pages.map((page) => {
                                        // 'Projects' needs a project picked on the landing page first
                                        const isDisabled = page.text === "Projects" && !selectedProjectId;
                                        return (
                                            <span className="tb-nav__slot" key={page.text} title={isDisabled ? "Please select a project below" : undefined}>
                                                <NavDropdownLinks
                                                    title={page.text}
                                                    icon={navIconFor(page.text)}
                                                    linkUrl={page.navUrl}
                                                    allProjectsList={allProjectsList}
                                                    disabled={isDisabled}
                                                />
                                            </span>
                                        );
                                    })}
                                </Box>

                                <Tooltip title={isSearchable ? '' : 'Press Enter to search issues'} placement="bottom">
                                    <div className={`tb-search ${search ? 'has-value' : ''}`}>
                                        <TextField
                                            inputRef={searchRef}
                                            className="tb-search__field"
                                            variant="outlined"
                                            placeholder={searchScope || 'Search Projam…'}
                                            type="search"
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Escape') {
                                                    setSearch('');
                                                    event.target.blur();
                                                    return;
                                                }
                                                if (event.key === 'Enter' && !isSearchable) handOffSearch();
                                            }}
                                            inputProps={{ 'aria-label': 'Search' }}
                                            InputProps={{
                                                startAdornment: (
                                                    <span className="tb-search__icon" aria-hidden="true">
                                                        <SearchRoundedIcon />
                                                    </span>
                                                ),
                                                endAdornment: search ? (
                                                    <button
                                                        type="button"
                                                        className="tb-search__clear"
                                                        aria-label="Clear search"
                                                        onClick={() => {
                                                            setSearch('');
                                                            searchRef.current?.focus();
                                                        }}
                                                    >
                                                        <CloseRoundedIcon />
                                                    </button>
                                                ) : (
                                                    <kbd className="tb-search__kbd" aria-hidden="true">/</kbd>
                                                ),
                                            }}
                                        />
                                    </div>
                                </Tooltip>

                                <Button className="tb-create" onClick={onChangeModalVerifyState} startIcon={<AddRoundedIcon />} disableRipple aria-label="Create issue">
                                    <span className="tb-create__label">Create</span>
                                </Button>

                                <NotificationComponent />

                                <button
                                    type="button"
                                    className={`tb-profile ${anchorElUser ? 'is-open' : ''}`}
                                    onClick={handleOpenUserMenu}
                                    aria-label="Open profile menu"
                                    aria-controls="tb-profile-menu"
                                    aria-haspopup="true"
                                    aria-expanded={Boolean(anchorElUser)}
                                >
                                    <span className="tb-profile__avatar">
                                        <BackgroundLetterAvatarsLogo
                                            LogoTitle={displayName}
                                            profileImage={currentUser?.profileImage || null}
                                        />
                                    </span>
                                    <KeyboardArrowDownRoundedIcon className="tb-profile__chevron" />
                                </button>
                                <Menu
                                    id="tb-profile-menu"
                                    anchorEl={anchorElUser}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    keepMounted
                                    open={Boolean(anchorElUser)}
                                    onClose={handleCloseUserMenu}
                                    slotProps={{ paper: { className: 'tb-menu tb-menu--profile' } }}
                                >
                                    <div className="tb-account" tabIndex={-1}>
                                        <span className="tb-account__avatar">
                                            <BackgroundLetterAvatarsLogo
                                                LogoTitle={displayName}
                                                profileImage={currentUser?.profileImage || null}
                                            />
                                        </span>
                                        <span className="tb-account__text">
                                            <strong>{displayName}</strong>
                                            {displayEmail ? <span>{displayEmail}</span> : null}
                                        </span>
                                    </div>
                                    <Divider />
                                    {settings.map((setting, index) => (
                                        <MenuItem
                                            key={setting}
                                            className="tb-menu__item"
                                            style={{ '--i': index + 1 }}
                                            onClick={() => handleMenuItemClick(setting)}
                                        >
                                            <span className="tb-menu__icon tb-menu__icon--green">
                                                <PersonRoundedIcon />
                                            </span>
                                            <span className="tb-menu__text">{setting === 'Profile' ? 'My profile' : setting}</span>
                                        </MenuItem>
                                    ))}
                                    <MenuItem
                                        className="tb-menu__item is-danger"
                                        style={{ '--i': settings.length + 1 }}
                                        onClick={() => {
                                            handleCloseUserMenu();
                                            onLogout();
                                        }}
                                    >
                                        <span className="tb-menu__icon">
                                            <LogoutRoundedIcon />
                                        </span>
                                        <span className="tb-menu__text">Log out</span>
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : token ? (
                            <div className="tb-auth">
                                <NavLink to='/projects' className="tb-auth__link">
                                    <Button className="tb-btn tb-btn--primary" startIcon={<DashboardRoundedIcon />} disableRipple>Dashboard</Button>
                                </NavLink>
                                <Button className="tb-btn tb-btn--ghost is-danger" startIcon={<LogoutRoundedIcon />} onClick={onLogout} disableRipple>Logout</Button>
                            </div>
                        ) : (
                            <div className="tb-auth">
                                <NavLink to="/Organisation" className="tb-auth__link">
                                    <Button className="tb-btn tb-btn--ghost" startIcon={<ApartmentRoundedIcon />} disableRipple>Organisation</Button>
                                </NavLink>
                                <NavLink to="/login" className="tb-auth__link">
                                    <Button className="tb-btn tb-btn--primary" startIcon={<LoginRoundedIcon />} disableRipple>Login</Button>
                                </NavLink>
                            </div>
                        )}
                    </Box>

                    {createModal && <CreateBacklogIssue isCreateNewBackIssuse={createModal} setIsCreateNewBackIssue={setCreateModel} />}
                </Toolbar>
            </Container>
        </AppBar>
    );
}
export default Header;


Header.propTypes = {
    pages: PropTypes.array.isRequired,
    isUserLoggedIn: PropTypes.bool.isRequired
};
